import { useEffect, useRef } from 'react';

import { cn } from '~/utils/cn';

interface PropsInputPinCode {
  value: string;
  onChange: (val: string) => void;
  length?: number;
  className?: string;
  autoFocus?: boolean;
}

export default function InputPinCode({
  value,
  onChange,
  length = 6,
  className,
  autoFocus,
}: PropsInputPinCode) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = value.split('').slice(0, length);
  while (digits.length < length) digits.push('');

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  const setDigit = (idx: number, digit: string) => {
    const clean = digit.replace(/\D/g, '').slice(0, 1);
    const next = [...digits];
    next[idx] = clean;
    onChange(next.join(''));
    if (clean && idx + 1 < length) refs.current[idx + 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const data = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!data) return;
    e.preventDefault();
    onChange(data.padEnd(length, '').slice(0, length));
    refs.current[Math.min(data.length, length - 1)]?.focus();
  };

  return (
    <div className={cn('flex items-center justify-center gap-nano', className)}>
      {Array.from({ length }).map((_, idx) => (
        <input
          key={idx}
          ref={(el) => {
            refs.current[idx] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[idx]}
          onChange={(e) => setDigit(idx, e.target.value)}
          onPaste={handlePaste}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
              refs.current[idx - 1]?.focus();
            }
          }}
          className="h-[56px] w-[44px] rounded-small bg-intense-02 border border-intense-03 text-center text-headingH2 color-complement-01 outline-none focus:border-primary-01"
        />
      ))}
    </div>
  );
}
